import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import {
  addToAlbum,
  artifactManagerService,
  getUserAlbums,
  mpsService,
  reportToSentry,
} from '@fs/zion-memories/memories-authorized-use-only'
import { useHistory } from '@fs/zion-router'
import { useEffectiveUser } from '@fs/zion-user'
import { useOverlay } from '@fs/zion-ui'
import { isExpectedError } from '../../sentryConfig'
import { convertObjectsToIds, getAlbumIdFromPath, filterOutMemories, isAlbumPath, paths } from './galleryUtils'

const AlbumsContext = createContext()
const AlbumsApiContext = createContext()

const albumActionTypes = {
  addAlbum: 'ADD_ALBUM',
  addBookmarkedAlbum: 'ADD_BOOKMARKED_ALBUM',
  addMemoriesToAlbums: 'ADD_MEMORIES_TO_ALBUMS',
  init: 'INIT',
  removeAlbum: 'REMOVE_ALBUM',
  removeBookmarkedAlbum: 'REMOVE_BOOKMARKED_ALBUM',
  removeMemoriesFromAlbum: 'REMOVE_MEMORIES_FROM_ALBUM',
  removeMemoriesFromAllAlbums: 'REMOVE_MEMORIES_FROM_ALL_ALBUMS',
  setAlbumError: 'SET_ALBUM_ERROR',
  setAlbumMemories: 'SET_ALBUM_MEMORIES',
  setAlbumsLoading: 'SET_ALBUMS_LOADING',
  setSelectedAlbum: 'SET_SELECTED_ALBUM',
  setSelectedAlbumId: 'SET_SELECTED_ALBUM_ID',
  updateAlbum: 'UPDATE_ALBUM',
  updateAlbumMemories: 'UPDATE_ALBUM_MEMORIES',
  updateAlbumMemoriesOrderMap: 'UPDATE_ALBUM_MEMORIES_ORDER_MAP',
}

/**
 * Transform the album order data array into a map for easy lookup. Use the memory id
 * as the key and the order data as the value.
 *
 * Example:
 * {
 *  1234: { id: 1, ordinal: 3, artifactId: 1234 }
 * }
 */
const createAlbumMemoriesOrderMap = (albumOrderData = []) =>
  albumOrderData.reduce((orderMap, orderItem) => {
    orderMap[orderItem.artifactId] = orderItem
    return orderMap
  }, {})

const getAlbumMemories = async (albumId) => {
  const [albumOrderResponse, albumMemoriesResponse] = await Promise.all([
    artifactManagerService.getAlbumOrdinals({ albumId }),
    mpsService.getAlbumMemories({
      albumId,
      queryParams: {
        includeAssociatedArtifacts: true,
        includeDatesPlaces: true,
        includeCiscData: true,
        _: Date.now(),
      },
    }),
  ])

  const albumMemories = albumMemoriesResponse?.data?.artifactList?.artifact
  const albumOrderData = albumOrderResponse?.data?.itemList?.item
  const memoriesOrderMap = createAlbumMemoriesOrderMap(albumOrderData)

  return { memories: albumMemories, memoriesOrderMap }
}

const sortAlbums = (a1, a2) => {
  if (a1.name.toLowerCase() < a2.name.toLowerCase()) return -1
  if (a1.name.toLowerCase() > a2.name.toLowerCase()) return 1
  return 0
}

const removeMemoriesFromOrderMap = (albumMemoriesOrderMap, removedMemoryIds) => {
  if (removedMemoryIds.length === 0) {
    return albumMemoriesOrderMap
  }

  const updatedOrderMap = { ...albumMemoriesOrderMap }
  removedMemoryIds.forEach((memoryId) => {
    delete updatedOrderMap[memoryId]
  })

  return updatedOrderMap
}

const findAlbum = (albums, albumId) => albums?.find((a) => a.id === albumId)
const findMemoryOrdinals = (albumMemoryOrdinals, albumId) => albumMemoryOrdinals?.find((o) => o.albumId === albumId)

const isAlbumError = (index, albumsResponses) =>
  albumsResponses?.[index]?.status === 'rejected' || albumsResponses?.[index]?.value?.albumsError

const albumReducer = (state, action) => {
  switch (action.type) {
    case albumActionTypes.addAlbum: {
      if (!action.newAlbum) return state

      const albums = [...state.albums, action.newAlbum].sort(sortAlbums)
      return { ...state, albums, albumMemoryIdsMap: { ...state.albumMemoryIdsMap, [action.newAlbum.id]: [] } }
    }
    case albumActionTypes.addBookmarkedAlbum:
      return { ...state, bookmarkedAlbums: [...state.bookmarkedAlbums, state.selectedAlbum] }
    case albumActionTypes.addMemoriesToAlbums: {
      const { albums, memories, albumMemoryOrdinals } = action

      const newAlbumMemoriesMap = {}
      const newAlbumMemoryIdsMap = { ...state.albumMemoryIdsMap }
      const newAlbumMemoriesOrderMap = { ...state.albumMemoriesOrderMap }

      /** Add the memories not already in each album and increment the memory count for each album. */
      albums.forEach((album) => {
        const albumMemoryIds = state.albumMemoryIdsMap?.[album.id] ?? []
        const memoriesNotInAlbum = memories.filter((memory) => !albumMemoryIds.includes(memory.id))

        /** Add the new memories to the album.
         *
         * NOTE:  (See Jira EOLBFRST-2188)
         * If the album isn't in `albumMemoriesMap`, then the user hasn't clicked on this
         * album link to load the albums memories. In this case, we shouldn't add the album to the
         * map because this will prevent the rest of the albums memories from being loaded when the
         * user clicks on the album link for the first because it will think the album memories have
         * already been loaded. */
        const albumMemories = state.albumMemoriesMap?.[album.id]
        if (albumMemories) {
          newAlbumMemoriesMap[album.id] = [...albumMemories, ...memoriesNotInAlbum]
        }

        /** Update the albumMemoryIdsMap for the album. */
        newAlbumMemoryIdsMap[album.id] = [...albumMemoryIds, ...memoriesNotInAlbum.map((memory) => memory.id)]

        /** Add/Update albumMemoriesOrderMap for the album. */
        const memoryOrdinals = findMemoryOrdinals(albumMemoryOrdinals, album.id)
        if (memoryOrdinals) {
          newAlbumMemoriesOrderMap[album.id] = createAlbumMemoriesOrderMap(memoryOrdinals.item)
        }

        /** Increment the memory count for the album. */
        const albumToUpdate = findAlbum(state.albums, album.id)
        albumToUpdate && (albumToUpdate.artifactCount += memoriesNotInAlbum.length)
      })

      const newState = {
        ...state,
        albumMemoriesMap: { ...state.albumMemoriesMap, ...newAlbumMemoriesMap },
        albumMemoriesOrderMap: newAlbumMemoriesOrderMap,
        albumMemoryIdsMap: { ...state.albumMemoryIdsMap, ...newAlbumMemoryIdsMap },
        albumErrorsMap: {},
        albums: [...state.albums],
      }

      return newState
    }
    case albumActionTypes.init:
      return {
        ...state,
        albums: action.albums,
        albumErrors: action.albumErrors,
        albumMemoryIdsMap: action.albumMemoryIdsMap,
        bookmarkedAlbums: action.bookmarkedAlbums,
        albumsLoading: false,
      }
    case albumActionTypes.removeAlbum: {
      if (!action.album) return state
      const newAlbumMemoryIdsMap = { ...state.albumMemoryIdsMap }
      const newAlbumMemoriesMap = { ...state.albumMemoriesMap }
      const newAlbumMemoriesOrderMap = { ...state.albumMemoriesOrderMap }

      /** Remove the deleted album from the ids map and memories map. */
      delete newAlbumMemoryIdsMap[action.album.id]
      delete newAlbumMemoriesMap[action.album.id]
      delete newAlbumMemoriesOrderMap[action.album.id]

      const albums = state.albums.filter((album) => album.id !== action.album.id)
      return {
        ...state,
        albums,
        selectedAlbum: null,
        selectedAlbumId: null,
        albumMemoryIdsMap: newAlbumMemoryIdsMap,
        albumMemoriesMap: newAlbumMemoriesMap,
        albumMemoriesOrderMap: newAlbumMemoriesOrderMap,
      }
    }
    case albumActionTypes.removeBookmarkedAlbum:
      return {
        ...state,
        bookmarkedAlbums: state.bookmarkedAlbums.filter((album) => album.id !== state.selectedAlbum.id),
      }
    case albumActionTypes.removeMemoriesFromAlbum: {
      /** Decrement the memory count for the selected album. */
      const album = findAlbum(state.albums, action.albumId)
      album && (album.artifactCount -= action.removedMemoryIds.length)

      /** Update albumMemoryIdsMap by filtering out the removed memory IDs for the specified album. */
      const newAlbumMemoryIdsMap = {
        ...state.albumMemoryIdsMap,
        [action.albumId]: state.albumMemoryIdsMap?.[action.albumId]?.filter(
          (id) => !action.removedMemoryIds.includes(id)
        ),
      }

      /** If the user has loaded the album memories, update albumMemoriesMap with the remaining memories for the specified album. */
      let newAlbumMemoriesMap = state.albumMemoriesMap
      if (state.albumMemoriesMap?.[action.albumId]) {
        /** Get all of the memories in the album. */
        const albumMemories = state.albumMemoriesMap[action.albumId] ?? []

        /** Filter out the memories removed from the album. */
        const remainingAlbumMemories = albumMemories.filter((memory) => !action.removedMemoryIds.includes(memory.id))

        newAlbumMemoriesMap = { ...state.albumMemoriesMap, [action.albumId]: remainingAlbumMemories }
      }

      /** Update albumMemoriesOrderMap by removing the order data for the removed memories. */
      let newAlbumMemoriesOrderMap = state.albumMemoriesOrderMap
      if (state.albumMemoriesOrderMap?.[action.albumId]) {
        const updatedOrderMap = removeMemoriesFromOrderMap(
          state.albumMemoriesOrderMap[action.albumId],
          action.removedMemoryIds
        )

        newAlbumMemoriesOrderMap = { ...state.albumMemoriesOrderMap, [action.albumId]: updatedOrderMap }

        /** If the all of the memories were removed from the order map, remove the order map for this album. */
        if (Object.keys(updatedOrderMap).length === 0) {
          delete newAlbumMemoriesOrderMap[action.albumId]
        }
      }

      return {
        ...state,
        albumMemoriesMap: newAlbumMemoriesMap,
        albumMemoriesOrderMap: newAlbumMemoriesOrderMap,
        albumMemoryIdsMap: newAlbumMemoryIdsMap,
        albumErrorsMap: {},
        albums: [...state.albums],
      }
    }
    case albumActionTypes.removeMemoriesFromAllAlbums: {
      const deletedIds = convertObjectsToIds(action.memories)

      /** Update albumMemoriesMap by filtering out the deleted memories. */
      const albumMemoriesMap = Object.keys(state.albumMemoriesMap).reduce(
        (map, albumId) => ({
          ...map,
          [albumId]: filterOutMemories(state.albumMemoriesMap?.[albumId], deletedIds),
        }),
        {}
      )

      /** Update albumMemoryIdsMap by filtering out the deleted memory IDs. */
      const albumMemoryIdsMap = Object.keys(state.albumMemoryIdsMap).reduce(
        (map, albumId) => ({
          ...map,
          [albumId]: state.albumMemoryIdsMap?.[albumId]?.filter((id) => !deletedIds.includes(id)),
        }),
        {}
      )

      /** Update albumMemoriesOrderMap by filtering out the deleted memory order data. */
      const albumMemoriesOrderMap = Object.keys(state.albumMemoriesOrderMap).reduce(
        (newAlbumMemoriesOrderMap, albumId) => {
          newAlbumMemoriesOrderMap[albumId] = removeMemoriesFromOrderMap(
            state.albumMemoriesOrderMap[albumId],
            deletedIds
          )
          return newAlbumMemoriesOrderMap
        },
        {}
      )

      /** Update albums with the new memory counts.
       *
       *  NOTE: We should also update bookmarked albums, but Artifact Manager doesn't have an endpoint to get
       * the list of memory ids for favorited albums. It only has one for user owned albums. */
      const albums = state.albums.map((album) => {
        album.artifactCount = albumMemoryIdsMap[album.id].length
        return album
      })

      return {
        ...state,
        albumErrorsMap: {},
        albumMemoriesMap,
        albumMemoriesOrderMap,
        albumMemoryIdsMap,
        albums,
      }
    }
    case albumActionTypes.setAlbumError: {
      const { albumId, albumError } = action
      return { ...state, albumErrorsMap: { ...state.albumErrorsMap, [albumId]: albumError } }
    }
    case albumActionTypes.setAlbumsLoading:
      return { ...state, albumsLoading: action.albumsLoading }
    case albumActionTypes.setAlbumMemories: {
      const { albumId, memories, memoriesOrderMap } = action
      return {
        ...state,
        albumMemoriesMap: { ...state.albumMemoriesMap, [albumId]: memories },
        albumMemoriesOrderMap: { ...state.albumMemoriesOrderMap, [albumId]: memoriesOrderMap },
        albumErrorsMap: { ...state.albumErrorsMap, [albumId]: false },
      }
    }
    case albumActionTypes.setSelectedAlbum:
      return { ...state, selectedAlbum: action.selectedAlbum, selectedAlbumId: action.selectedAlbum?.id }
    case albumActionTypes.setSelectedAlbumId: {
      const { selectedAlbumId } = action
      const selectedAlbum = findAlbum([...state.albums, ...state.bookmarkedAlbums], selectedAlbumId)
      return { ...state, selectedAlbumId, selectedAlbum }
    }
    case albumActionTypes.updateAlbumMemories:
      return {
        ...state,
        /**
         * If the updated memory is in an album, then it needs to be replaced in the albumMemoriesMap.
         */
        albumMemoriesMap: Object.keys(state.albumMemoriesMap).reduce((map, albumId) => {
          const memoryInAlbum = state.albumMemoriesMap?.[albumId]?.some(
            (albumMemory) => albumMemory?.id === action?.memory?.id
          )
          return memoryInAlbum
            ? {
                ...map,
                [albumId]: state.albumMemoriesMap?.[albumId]?.map((memory) =>
                  memory?.id === action?.memory?.id ? action?.memory : memory
                ),
              }
            : map
        }, {}),
      }
    case albumActionTypes.updateAlbum: {
      if (!action.updatedAlbum) return state

      const editedAlbumIndex = state.albums.findIndex((existingAlbum) => existingAlbum.id === action.updatedAlbum.id)
      const newAlbums = [...state.albums]
      newAlbums[editedAlbumIndex] = action.updatedAlbum
      const sortedNewAlbums = newAlbums.sort(sortAlbums)
      // Get the updated index to correctly update state, because editedAlbumIndex might not be pointing to the correct album after sorting
      const updatedAlbumIndex = sortedNewAlbums.findIndex((album) => album.id === action.updatedAlbum.id)

      return {
        ...state,
        albums: sortedNewAlbums,
        selectedAlbum: sortedNewAlbums[updatedAlbumIndex],
        selectedAlbumId: sortedNewAlbums[updatedAlbumIndex]?.id,
      }
    }
    case albumActionTypes.updateAlbumMemoriesOrderMap: {
      const { albumId, memoriesOrderMap } = action
      const newAlbumMemoriesOrderMap = { ...state.albumMemoriesOrderMap, [albumId]: { ...memoriesOrderMap } }
      return { ...state, albumMemoriesOrderMap: newAlbumMemoriesOrderMap }
    }
    default:
      return state
  }
}

const initialState = {
  albumErrors: false,
  albumErrorsMap: {},
  albumMemoriesMap: {},
  albumMemoriesOrderMap: {},
  albumMemoryIdsMap: {},
  albums: [],
  albumsLoading: true,
  bookmarkedAlbums: [],
  selectedAlbum: null,
  selectedAlbumId: null,
}

const AlbumsProvider = ({
  children,
  galleryActions,
  isAlbumPage,
  isMobile,
  isMyMemoriesPage,
  memoriesLoading,
  activePage,
}) => {
  const isCurrent = useRef(true)
  const [state, dispatch] = useReducer(albumReducer, initialState)
  const history = useHistory()
  const { cisId, signedIn } = useEffectiveUser()
  const editAlbumOverlay = useOverlay()

  const { isOpen: editAlbumOverlayOpen } = editAlbumOverlay
  const {
    addMemories,
    removeMemories,
    setActivePage,
    setMemoriesLoading,
    setSelectMode,
    updateMemories,
    updateMemory,
  } = galleryActions

  const enableDragAndDrop = useMemo(
    () => !isMobile && signedIn && !editAlbumOverlayOpen,
    [editAlbumOverlayOpen, isMobile, signedIn]
  )

  const allAlbums = useMemo(() => [...state.albums, ...state.bookmarkedAlbums], [state.albums, state.bookmarkedAlbums])

  const selectedAlbumIsBookmarked = useMemo(
    () => state.bookmarkedAlbums?.some((album) => album.id === state.selectedAlbumId),
    [state.bookmarkedAlbums, state.selectedAlbumId]
  )

  const albumActions = useMemo(
    () => ({
      addAlbum: (newAlbum) => dispatch({ type: albumActionTypes.addAlbum, newAlbum }),
      addBookmarkedAlbum: () => dispatch({ type: albumActionTypes.addBookmarkedAlbum }),
      addMemoriesToAlbums: ({ albums, memories, albumMemoryOrdinals }) =>
        dispatch({ type: albumActionTypes.addMemoriesToAlbums, albums, memories, albumMemoryOrdinals }),
      init: ({ albums, albumErrors, albumMemoryIdsMap, bookmarkedAlbums }) =>
        dispatch({ type: albumActionTypes.init, albums, albumErrors, albumMemoryIdsMap, bookmarkedAlbums }),
      removeAlbum: (album) => dispatch({ type: albumActionTypes.removeAlbum, album }),
      removeBookmarkedAlbum: () => dispatch({ type: albumActionTypes.removeBookmarkedAlbum }),
      removeMemoriesFromAlbum: ({ albumId, removedMemoryIds }) =>
        dispatch({ type: albumActionTypes.removeMemoriesFromAlbum, albumId, removedMemoryIds }),
      removeMemoriesFromAllAlbums: ({ memories }) =>
        dispatch({ type: albumActionTypes.removeMemoriesFromAllAlbums, memories }),
      setAlbumError: ({ albumId, albumError }) =>
        dispatch({ type: albumActionTypes.setAlbumError, albumId, albumError }),
      setAlbumsLoading: (albumsLoading) => dispatch({ type: albumActionTypes.setAlbumsLoading, albumsLoading }),
      setAlbumMemories: ({ albumId, memories, memoriesOrderMap }) =>
        dispatch({ type: albumActionTypes.setAlbumMemories, albumId, memories, memoriesOrderMap }),
      setSelectedAlbum: (selectedAlbum) => dispatch({ type: albumActionTypes.setSelectedAlbum, selectedAlbum }),
      setSelectedAlbumId: (selectedAlbumId) => dispatch({ type: albumActionTypes.setSelectedAlbumId, selectedAlbumId }),
      updateAlbumMemories: (memory) => dispatch({ type: albumActionTypes.updateAlbumMemories, memory }),
      updateAlbum: (updatedAlbum) => dispatch({ type: albumActionTypes.updateAlbum, updatedAlbum }),
      updateAlbumMemoriesOrderMap: ({ albumId, memoriesOrderMap }) =>
        dispatch({ type: albumActionTypes.updateAlbumMemoriesOrderMap, albumId, memoriesOrderMap }),
    }),
    []
  )

  const fetchAlbums = useCallback(async (apiParams) => {
    let albums = []
    let albumsError = false
    try {
      albums = (await getUserAlbums(apiParams)) ?? []
    } catch (error) {
      albumsError = true
    }
    return { albums, albumsError }
  }, [])

  const fetchMemoryIdsInAlbums = useCallback(async (apiParams) => {
    let memoryIdsInAlbums = []
    try {
      memoryIdsInAlbums = (await artifactManagerService.getMemoryIdsInAlbums(apiParams)) ?? []
    } catch (error) {
      reportToSentry(error, { operation: 'getMemoryIdsInAlbums' })
    }
    return memoryIdsInAlbums
  }, [])

  const albumMemories = useMemo(
    () => state.albumMemoriesMap?.[state.selectedAlbumId] ?? [],
    [state.albumMemoriesMap, state.selectedAlbumId]
  )

  const isMemoryInAnyAlbum = useCallback(
    (memoryId) => Object.values(state.albumMemoryIdsMap).some((memoryIds) => memoryIds.includes(memoryId)),
    [state.albumMemoryIdsMap]
  )

  /**
   * Batch version of isMemoryInAnyAlbum - checks multiple memories at once
   * Returns a Set of memory IDs that are in at least one album
   * OPTIMIZED: Build a Set of ALL memory IDs in albums once, then check membership (O(n) instead of O(n*m))
   */
  const getMemoriesInAlbums = useCallback(
    (memoryIds) => {
      // Build a Set of all memory IDs that are in ANY album
      const allMemoriesInAlbums = new Set()
      Object.values(state.albumMemoryIdsMap).forEach((albumMemoryIds) => {
        albumMemoryIds.forEach((memoryId) => allMemoriesInAlbums.add(memoryId))
      })

      // Filter the input memories to only those in the Set
      const memoriesInAlbums = new Set()
      memoryIds.forEach((memoryId) => {
        if (allMemoriesInAlbums.has(memoryId)) {
          memoriesInAlbums.add(memoryId)
        }
      })

      return memoriesInAlbums
    },
    [state.albumMemoryIdsMap]
  )

  /**
   * This gallery callback is defined here since it only alters album related state.
   */
  const addToAlbumsCallback = useCallback(
    (params) => {
      albumActions.addMemoriesToAlbums(params)
      setSelectMode(false)
    },
    [albumActions, setSelectMode]
  )

  const removeFromAlbumCallback = useCallback(
    (params) => {
      albumActions.removeMemoriesFromAlbum(params)
      setSelectMode(false)
    },
    [albumActions, setSelectMode]
  )

  /**
   * Callback for adding memories to the Gallery and selected album after uploading.
   */
  const addCallback = useCallback(
    async ({ memories }) => {
      if (state.selectedAlbum) {
        const albumResults = await addToAlbum({ albums: [state.selectedAlbum], memories })
        albumResults.successful.length && albumActions.addMemoriesToAlbums({ albums: [state.selectedAlbum], memories })
      }

      addMemories({ memories })

      /** If memories were added on a page that isn't My Memories or an album, navigate to the My Memories page. */
      if (!isMyMemoriesPage && !isAlbumPage) {
        const path = paths.gallery
        history.push(path)
        setActivePage(path)
      }
    },
    [addMemories, albumActions, history, isAlbumPage, isMyMemoriesPage, setActivePage, state.selectedAlbum]
  )

  /**
   * This gallery callback needs to be in the album context and not the gallery context because
   * if a memory in an album is deleted it needs to handle filtering that out of state too.
   */
  const deleteCallback = useCallback(
    (params) => {
      albumActions.removeMemoriesFromAllAlbums(params)
      removeMemories(params)
    },
    [albumActions, removeMemories]
  )

  /**
   * This gallery callback needs to be in the album context in case the memory
   * that is updated is also in an album.
   */
  const updateCallback = useCallback(
    (updatedMemories) => {
      if (updatedMemories.length === 1) {
        const inAlbum = isMemoryInAnyAlbum(updatedMemories[0]?.id)
        inAlbum && albumActions.updateAlbumMemories(updatedMemories[0])
        updateMemory(updatedMemories[0])
      } else {
        // OPTIMIZED: Batch check which memories are in albums
        const memoriesInAlbums = getMemoriesInAlbums(updatedMemories.map((m) => m.id))

        // Only update album memories for those actually in albums
        updatedMemories.forEach((updatedMemory) => {
          if (memoriesInAlbums.has(updatedMemory.id)) {
            albumActions.updateAlbumMemories(updatedMemory)
          }
        })

        updateMemories(updatedMemories)
      }
    },
    [albumActions, isMemoryInAnyAlbum, getMemoriesInAlbums, updateMemories, updateMemory]
  )

  const handleEditSuccess = useCallback(
    (updatedMemories) => {
      updateCallback(updatedMemories)
      galleryActions.endEditing()
      galleryActions.setSelectMode(false)
    },
    [galleryActions, updateCallback]
  )

  useEffect(() => {
    const loadAlbums = async () => {
      if (cisId) {
        albumActions.setAlbumsLoading(true)

        const respIndexes = {
          userAlbums: 0,
          bookmarkedAlbums: 1,
          memoryIdsInAlbums: 2,
        }
        const albumsResponses = await Promise.allSettled([
          fetchAlbums({ cisId }),
          fetchAlbums({ cisId, bookmarked: true }),
          fetchMemoryIdsInAlbums({ cisId }),
        ])

        const newAlbums = albumsResponses?.[respIndexes.userAlbums]?.value?.albums
        const newBookmarkedAlbums = albumsResponses?.[respIndexes.bookmarkedAlbums]?.value?.albums
        const newAlbumMemoryIdsMap = albumsResponses?.[respIndexes.memoryIdsInAlbums]?.value?.data?.reduce(
          (acc, { artifactId: memoryId, albumIds }) => {
            albumIds.forEach((albumId) => {
              if (!acc[albumId]) {
                acc[albumId] = []
              }
              acc[albumId].push(memoryId)
            })
            return acc
          },
          {}
        )

        /**
         * At this point, the newAlbumMemoryIdsMap does not include entries for albumIds without any memories.
         * This creates entries for those albumIds with an empty array as the value.
         */
        const allAlbumIds = newAlbums.map((album) => album.id)
        const albumMemoryIdsMap = newAlbumMemoryIdsMap ?? {}
        allAlbumIds.forEach((albumId) => {
          if (!albumMemoryIdsMap[albumId]) {
            albumMemoryIdsMap[albumId] = []
          }
        })

        isCurrent.current &&
          albumActions.init({
            albums: newAlbums,
            albumErrors:
              isAlbumError(respIndexes.userAlbums, albumsResponses) ||
              isAlbumError(respIndexes.bookmarkedAlbums, albumsResponses),
            albumMemoryIdsMap,
            bookmarkedAlbums: newBookmarkedAlbums,
          })
      } else {
        albumActions.setAlbumsLoading(false)
      }
    }

    loadAlbums()
  }, [albumActions, cisId, fetchAlbums, fetchMemoryIdsInAlbums])

  const loadAlbumMemories = useCallback(
    async (albumId) => {
      if (!memoriesLoading && !state.albumMemoriesMap[albumId] && !state.albumErrorsMap[albumId]) {
        try {
          setMemoriesLoading(true)
          const { memories, memoriesOrderMap } = await getAlbumMemories(albumId)
          isCurrent.current && albumActions.setAlbumMemories({ albumId, memories, memoriesOrderMap })
        } catch (error) {
          /**
           * Expected 4xx errors for albums outside the user's list are skipped (mostly crawlers and stale links).
           * Listed albums should exist, so their failures are always reported.
           */
          isCurrent.current && albumActions.setAlbumError({ albumId, albumError: true })
          const isListedAlbum = allAlbums.some((album) => album.id === albumId)
          if (isListedAlbum || !isExpectedError(error)) {
            reportToSentry(error, { operation: 'getAlbumMemories', albumId })
          }
        } finally {
          isCurrent.current && setMemoriesLoading(false)
        }
      }
    },
    [albumActions, allAlbums, memoriesLoading, setMemoriesLoading, state.albumErrorsMap, state.albumMemoriesMap]
  )

  /**
   * The purpose of this useEffect is to load the album data and set the album as the `selectedAlbum` only
   * when the browser is loading an album page directly and not because the user clicked on an album link.
   * Clicking on an album link is handled in GalleryExplorer.
   *
   * This will get the album id from the url when the page first loads, load the album data, and then set
   * the album as the `selectedAlbum`
   */
  useEffect(() => {
    const loadAlbumFromUrl = async () => {
      const pathname = history.location.pathname
      const albumId = getAlbumIdFromPath(pathname)

      /**
       * If this album is already set as the selected album, then there's nothing more to do, so just return.
       * This would be the case if this useEffect has already run, or the user has already clicked on the link
       * for this album.
       */
      if (albumId === state.selectedAlbum?.id) {
        return
      }

      /**
       * If the albumId in the URL isn't the `selectedAlbum` then load the album data and set it as
       * the `selectedAlbum`.
       */
      if (albumId) {
        try {
          const response = await artifactManagerService.getAlbum({ albumId })
          const album = response.data

          if (isCurrent.current) {
            albumActions.setSelectedAlbum(album)
          }
        } catch (error) {
          if (!isExpectedError(error)) reportToSentry(error, { operation: 'getAlbum', albumId })
        }
      }
    }

    loadAlbumFromUrl()
  }, [albumActions, allAlbums, history.location.pathname, state.selectedAlbum?.id])

  /**
   * The purpose of this useEffect is to load the album memories only when the browser is loading an album
   * page directly and not because the user clicked on an album link. Clicking on an album link is handled
   * in GalleryExplorer.
   *
   * This will get the album id from the url when the page first loads, and then load the album's memories.
   */
  useEffect(() => {
    const pathname = history.location.pathname
    const albumId = getAlbumIdFromPath(pathname)

    /**
     * If the memories for this album are already loaded, then just return.
     *
     * Albums that failed to load are skipped too. A failed load only records the album in
     * `albumErrorsMap`, never in `albumMemoriesMap`, and the `finally` in `loadAlbumMemories` flips
     * `memoriesLoading` back to false — which gives `loadAlbumMemories` a new identity and re-runs this
     * effect. Without the error check the request would be retried indefinitely.
     */
    if (state.albumMemoriesMap[albumId] || state.albumErrorsMap[albumId]) {
      return
    }

    if (albumId && !memoriesLoading) {
      loadAlbumMemories(albumId)
    } else if (isAlbumPath(activePage) && !albumId) {
      /** If the album path is missing the albumId, navigate to My Memories collection. */
      isCurrent.current && setActivePage(paths.gallery)
      history.replace(`${paths.gallery}${window.location.search ? `/${window.location.search}` : ''}`)
    }
  }, [
    activePage,
    history,
    loadAlbumMemories,
    memoriesLoading,
    setActivePage,
    state.albumErrorsMap,
    state.albumMemoriesMap,
  ])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  const apiValues = useMemo(
    () => ({
      ...albumActions,
      addCallback,
      addToAlbumsCallback,
      deleteCallback,
      findAlbum,
      handleEditSuccess,
      isMemoryInAnyAlbum,
      loadAlbumMemories,
      removeFromAlbumCallback,
      updateCallback,
    }),
    [
      addCallback,
      addToAlbumsCallback,
      albumActions,
      deleteCallback,
      handleEditSuccess,
      isMemoryInAnyAlbum,
      loadAlbumMemories,
      removeFromAlbumCallback,
      updateCallback,
    ]
  )

  const {
    albumErrors,
    albumErrorsMap,
    albumMemoriesMap,
    albumMemoriesOrderMap,
    albumMemoryIdsMap,
    albums,
    albumsLoading,
    bookmarkedAlbums,
    selectedAlbum,
    selectedAlbumId,
  } = state

  const dataValues = useMemo(
    () => ({
      albumErrors,
      albumErrorsMap,
      albumMemories,
      albumMemoriesMap,
      albumMemoriesOrderMap,
      albumMemoryIdsMap,
      albums,
      albumsLoading,
      bookmarkedAlbums,
      editAlbumOverlay,
      enableDragAndDrop,
      selectedAlbum,
      selectedAlbumId,
      selectedAlbumIsBookmarked,
    }),
    [
      albumErrors,
      albumErrorsMap,
      albumMemories,
      albumMemoriesMap,
      albumMemoriesOrderMap,
      albumMemoryIdsMap,
      albums,
      albumsLoading,
      bookmarkedAlbums,
      editAlbumOverlay,
      enableDragAndDrop,
      selectedAlbum,
      selectedAlbumId,
      selectedAlbumIsBookmarked,
    ]
  )

  return (
    <AlbumsContext.Provider value={dataValues}>
      <AlbumsApiContext.Provider value={apiValues}>{children}</AlbumsApiContext.Provider>
    </AlbumsContext.Provider>
  )
}

export { AlbumsApiContext, AlbumsContext, AlbumsProvider }
